import { useCallback, useReducer, useRef } from "react";
import { useMutation, type UseMutateFunction } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import { chatService } from "../services/chatService";
import {
  applyErrorResponseToChat,
  DATA_SOURCES,
  normalizeChatMessage,
} from "../components/Chat/utils";
import type { AskVivoSendMessageOptions } from "../components/AskVivo/askVivo.types";
import type { ChatMessage } from "../components/Chat/chat";

export interface AskVivoEngineState {
  chatHistory: ChatMessage[];
  sessionId: string;
}

interface SendVariables {
  payload: {
    session_id: string;
    prompt: string;
    trial_id: string;
    scope: "trial" | "portfolio";
    filters: {
      data_sources: { includes: readonly string[]; excludes: string[] };
    };
  };
  optimisticMessage: ChatMessage;
  requestEpoch: number;
}

type AskVivoEngineAction =
  | { type: "SEND_START"; optimisticMessage: ChatMessage }
  | { type: "SEND_SUCCESS"; updatedMessage: ChatMessage; epoch: number }
  | { type: "SEND_ERROR"; errorMessage: ChatMessage; epoch: number }
  | { type: "LOAD_SESSION"; messages: ChatMessage[]; sessionId: string }
  | { type: "SET_SESSION_ID"; sessionId: string }
  | { type: "RESET"; sessionId: string };

export function askVivoEngineReducer(
  state: AskVivoEngineState,
  action: AskVivoEngineAction,
): AskVivoEngineState {
  switch (action.type) {
    case "SEND_START":
      return {
        ...state,
        chatHistory: [...state.chatHistory, action.optimisticMessage],
      };
    case "SEND_SUCCESS": {
      const chatHistory = state.chatHistory.map((message) =>
        message._optimisticEpoch === action.epoch
          ? action.updatedMessage
          : message,
      );
      return { ...state, chatHistory };
    }
    case "SEND_ERROR": {
      const chatHistory = state.chatHistory.map((message) =>
        message._optimisticEpoch === action.epoch
          ? action.errorMessage
          : message,
      );
      return { ...state, chatHistory };
    }
    case "LOAD_SESSION":
      return { chatHistory: action.messages, sessionId: action.sessionId };
    case "SET_SESSION_ID":
      return { ...state, sessionId: action.sessionId };
    case "RESET":
      return { chatHistory: [], sessionId: action.sessionId };
    default:
      return state;
  }
}

export function useAskVivoEngine() {
  const [state, dispatch] = useReducer(askVivoEngineReducer, {
    chatHistory: [],
    sessionId: uuidv4(),
  });
  const requestEpochRef = useRef(0);
  const mutateRef = useRef<UseMutateFunction<
    unknown,
    Error,
    SendVariables,
    void
  > | null>(null);
  const resetMutationRef = useRef<(() => void) | null>(null);

  const { mutate, reset, isPending } = useMutation({
    mutationFn: ({ payload }: SendVariables) =>
      chatService.sendMessage(payload),
    onMutate: ({ optimisticMessage }) => {
      dispatch({ type: "SEND_START", optimisticMessage });
    },
    onSuccess: (data, variables) => {
      if (variables.requestEpoch !== requestEpochRef.current) {
        return;
      }
      dispatch({
        type: "SEND_SUCCESS",
        updatedMessage: normalizeChatMessage(data as any),
        epoch: variables.requestEpoch,
      });
    },
    onError: (error, { optimisticMessage, requestEpoch }) => {
      if (requestEpoch !== requestEpochRef.current) {
        return;
      }
      const errorMessage = { ...optimisticMessage };
      applyErrorResponseToChat(errorMessage, error);
      dispatch({ type: "SEND_ERROR", errorMessage, epoch: requestEpoch });
    },
  });

  mutateRef.current = mutate;
  resetMutationRef.current = reset;

  const sendMessage = useCallback(
    (userPrompt: string, options: AskVivoSendMessageOptions = {}) => {
      if (!userPrompt.trim()) return;

      const effectiveSessionId = options.sessionId ?? state.sessionId;
      const selectedSources = options.selectedSources ?? {};
      const requestEpoch = ++requestEpochRef.current;
      const fullPrompt = options.promptContext
        ? `[Context]\n${options.promptContext}\n[/Context]\n${userPrompt}`
        : userPrompt;

      if (effectiveSessionId !== state.sessionId) {
        dispatch({ type: "SET_SESSION_ID", sessionId: effectiveSessionId });
      }

      const optimisticMessage: ChatMessage = {
        ...normalizeChatMessage({
          interaction_id: "",
          session_id: effectiveSessionId,
          input: userPrompt,
          output: "",
          selected_sources: selectedSources,
          timestamp: "",
          requested_clarification: false,
          isLoading: true,
          hasError: false,
        }),
        _optimisticEpoch: requestEpoch,
      };

      mutateRef.current?.({
        payload: {
          session_id: effectiveSessionId,
          prompt: fullPrompt,
          trial_id: options.trialId ?? "",
          scope: options.scope ?? "trial",
          filters: {
            data_sources: { includes: DATA_SOURCES, excludes: [] },
          },
        },
        optimisticMessage,
        requestEpoch,
      });
    },
    [state.sessionId],
  );

  const resetSession = useCallback((): string => {
    requestEpochRef.current += 1;
    resetMutationRef.current?.();
    const newSessionId = uuidv4();
    dispatch({ type: "RESET", sessionId: newSessionId });
    return newSessionId;
  }, []);

  const loadSession = useCallback(
    (messages: ChatMessage[], sessionId: string) => {
      dispatch({ type: "LOAD_SESSION", messages, sessionId });
    },
    [],
  );

  return {
    chatHistory: state.chatHistory,
    sessionId: state.sessionId,
    isChatLoading: isPending,
    sendMessage,
    resetSession,
    loadSession,
  };
}
